import { Link } from "react-router-dom";
import { PUBLIC_ROUTES } from "../../routes";
import MarketingShell from "./MarketingShell";

/**
 * Ground Truth — an original explainer of the six physical problems every
 * caring humanoid runs into, and where the Manifold trust layer clips onto
 * each. Concept diagrams are original ASCII renders in the site palette.
 */

interface Essential {
  key: string;
  title: string;
  body: string;
  clip: string;
  img: string;
  imgAlt: string;
  barLeft: string;
  barRight: string;
  barFoot: string;
}

const ESSENTIALS: Essential[] = [
  {
    key: "Power",
    title: "A cord is not a deployment.",
    body: "A humanoid carries under two kilowatt-hours and spends most of it just holding itself upright &mdash; a few hundred watts standing, more the moment it lifts. That buys two to four hours before it has to sit down and charge, against a shift that runs eight. Until the cells improve or the pack hot-swaps in seconds, energy, not intelligence, is the ceiling the whole field runs into.",
    clip: "Our reflex and planner are budgeted in single-digit watts, so the mind we add never eats the runtime the body needs.",
    img: "/ascii-ess-power.png",
    imgAlt: "A robot holding a battery beside a runtime gauge that is only a third full against a full-shift bar, rendered in ASCII characters",
    barLeft: "mk-vision // power budget",
    barRight: "the runtime gap",
    barFoot: "2–4 hr charge · 8 hr shift",
  },
  {
    key: "Perception",
    title: "It builds a model, it doesn&rsquo;t watch a video.",
    body: "No one camera is enough, so a caring humanoid fuses many &mdash; colour from lenses across the head, torso and palms, distance from depth, motion from an inner-ear sensor, its own pose from the joints &mdash; into a single estimate of the room and where it stands in it. Each stream is noisy alone; the fusion is what makes it safe to act on. Perception is the fusion, not the camera.",
    clip: "That fused model lives on the robot. The cloud only ever sees a signed abstraction of it &mdash; &ldquo;a person in the doorway&rdquo;, never the footage.",
    img: "/ascii-ess-perception.png",
    imgAlt: "A robot studded with sensor nodes, rays converging into a central fusion node that outputs a small 3D room model, rendered in ASCII characters",
    barLeft: "mk-vision // sensor fusion",
    barRight: "one estimate",
    barFoot: "cameras + depth + imu + joints",
  },
  {
    key: "Balance",
    title: "Standing is an argument the robot wins every few milliseconds.",
    body: "Two feet make a small base and the mass sits high above it, so a humanoid is always a little bit falling. A controller keeps the ground&rsquo;s push inside the footprint, correcting hundreds of times a second with the same three reflexes you use &mdash; rock at the ankle, bend at the hip, or take a step. Balance is a loop, not a pose.",
    clip: "That loop is exactly where a caring robot hurts someone if it drops a beat, so our safety envelope guards it &mdash; force-limited, contact-aware, and running even if everything above it goes dark.",
    img: "/ascii-ess-balance.png",
    imgAlt: "A robot with a centre-of-mass dot plumbed to a balance point inside its support polygon, beside a sense-predict-correct loop, rendered in ASCII characters",
    barLeft: "mk-vision // balance loop",
    barRight: "sense → predict → correct",
    barFoot: "100s–1000s of times a second",
  },
  {
    key: "Compute",
    title: "The brain has to ride inside the body.",
    body: "Contact happens in milliseconds; a round-trip to a datacentre takes hundreds. Wait on the network and the robot has already crushed the egg or missed the catch &mdash; so perception, the policy, and the control loop all run on a chip bolted into the torso, inside a power budget that competes with the actuators for the same battery.",
    clip: "Our on-device reflex answers in well under a millisecond, and by keeping the heavy thinking local we keep the home&rsquo;s video in the home.",
    img: "/ascii-ess-compute.png",
    imgAlt: "A robot torso with a glowing chip inside, a tug-of-war between compute and actuators over one battery, and a latency meter, rendered in ASCII characters",
    barLeft: "mk-vision // onboard compute",
    barRight: "under 10 ms, local",
    barFoot: "model size vs latency vs power",
  },
  {
    key: "Sim-to-Real",
    title: "Ten thousand rehearsals for a room it has never seen.",
    body: "A policy can&rsquo;t practise on a real person, so it learns in simulation &mdash; then faces a reality the simulator never quite matched. The fix isn&rsquo;t a more perfect simulator; it&rsquo;s a more varied one. Randomise gravity, friction, delay and light across thousands of parallel copies, centred on a scan of the actual room, and the real world becomes just one more variant the policy already handles.",
    clip: "This is the RoboTwins core we&rsquo;re built on. We scan the real room into the twin, prove the policy inside a safety envelope there, and never let it onto hardware until the floor holds.",
    img: "/ascii-ess-sim2real.png",
    imgAlt: "A field of faint parallel simulated robots funnelling through a reality-gap gate into one solid robot standing in a real room, rendered in ASCII characters",
    barLeft: "mk-vision // domain randomisation",
    barRight: "closing the reality gap",
    barFoot: "thousands of twins → one floor",
  },
  {
    key: "The Brain",
    title: "Three teachers, one mind.",
    body: "The body is maturing faster than the mind that drives it. Three kinds of learning are converging into one stack: imitation, where a vision-language-action model copies human demonstrations; reinforcement, where the robot practises in simulation until the reflex is sharp; and world models, which predict what the next second looks like so the robot can plan by imagining consequences.",
    clip: "Whichever way that bet lands, a caring humanoid can&rsquo;t ship on cleverness alone. We add the layer that makes any of these minds auditable and insurable &mdash; a signed record of every decision it makes.",
    img: "/ascii-ess-brain.png",
    // Plain characters only: alt is a real attribute, so entities would be
    // read out literally.
    imgAlt: "Three learning nodes, imitate, practise and imagine, feeding one central policy core that drives a robot, rendered in ASCII characters",
    barLeft: "mk-vision // the robot brain",
    barRight: "vla + rl + world model",
    barFoot: "one stack, one signed record",
  },
];

export default function Essentials() {
  return (
    <MarketingShell pageClass="mk-care-page mk-ess-page">
      <section className="mk-how-hero">
        <p className="sg-eyebrow">Ground truth</p>
        <h1>The six problems every caring humanoid runs into &mdash; and where we clip on.</h1>
        <p className="mk-care-lede">
          Before a humanoid can be trusted in a home or a factory, it has to
          solve six hard physical problems that have nothing to do with
          being clever: staying powered, seeing the room, staying upright,
          thinking fast enough, learning somewhere safe, and having a mind
          at all. Manifold Kinetics doesn&rsquo;t build the robot that solves
          them &mdash; we build the trust layer that clips onto each one, so
          the result is safe, private, and insurable. Here&rsquo;s the ground
          the whole field stands on, and exactly where we sit.
        </p>
      </section>

      <section className="mk-ess-rows">
        {ESSENTIALS.map((item, i) => (
          <article className="mk-ess-row" data-flip={i % 2 === 1} key={item.key}>
            <div className="mk-ess-copy">
              <p className="sg-section-kicker">
                {String(i + 1).padStart(2, "0")} &middot; {item.key}
              </p>
              <h2 dangerouslySetInnerHTML={{ __html: item.title }} />
              <p
                className="mk-ess-body"
                dangerouslySetInnerHTML={{ __html: item.body }}
              />
              <p className="mk-ess-clip">
                <span>Where we clip in</span>
                <span dangerouslySetInnerHTML={{ __html: item.clip }} />
              </p>
            </div>
            <div className="mk-ess-art">
              <figure className="mk-art-frame">
                <div className="mk-art-bar" aria-hidden="true">
                  <span>{item.barLeft}</span>
                  <span>
                    <em>{item.barRight}</em>
                  </span>
                </div>
                <img src={item.img} alt={item.imgAlt} loading="lazy" />
                <div className="mk-art-bar bottom" aria-hidden="true">
                  <span>
                    <em>&#10003;</em> field note
                  </span>
                  <span>{item.barFoot}</span>
                </div>
              </figure>
            </div>
          </article>
        ))}
      </section>

      <section className="mk-ess-credit">
        <p>
          Further reading: these six constraints are covered well in the
          public &ldquo;Robotics essentials&rdquo; series by{" "}
          <a
            href="https://x.com/LeoKharon"
            target="_blank"
            rel="noopener noreferrer"
          >
            @LeoKharon
          </a>
          . The writing and diagrams on this page are our own; the underlying
          physics belongs to everyone.
        </p>
      </section>

      <section className="sg-final-cta">
        <p className="sg-section-kicker">Where the trust layer sits</p>
        <h2>
          None of this is optional physics &mdash; and none of it ships
          without a record someone can check.
        </h2>
        <p>
          Every one of these six problems is a place a caring humanoid can
          fail quietly. Manifold Kinetics is the layer that turns each of
          them into something a family, a plant manager, and an insurer can trust:
          an on-device reflex for the fast failures, a signed ledger for the
          slow ones, and a world twin to prove it all before a real robot
          moves.
        </p>
        <div className="sg-hero-actions center">
          <Link
            to={PUBLIC_ROUTES.howItWorks}
            className="sg-btn sg-btn-primary"
          >
            See how the layer works
          </Link>
          <Link
            to={PUBLIC_ROUTES.contact}
            className="sg-btn sg-btn-secondary"
          >
            Request a pilot
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
